/**
 * Face-to-ID Transition Owner
 *
 * Single flow-level authority for the transition from a terminal face
 * verification result into the document (ID) verification workflow.
 *
 * Ownership contract:
 * - Accepts exactly one terminal face result per capture attempt and latches
 *   the attempt terminal. Duplicate or stale-attempt results are rejected.
 * - Owns automatic retrieval of the required Portal best-selfie handoff
 *   artifact. Retrieval is keyed on the session ID and runs whenever document
 *   verification is required, regardless of the transport `selfie_stored`
 *   flag (that flag is server-side storage confirmation and diagnostics, not
 *   fetch permission or proof of browser readiness).
 * - Records attempt-scoped readiness. `sessionStorage` remains a same-browser
 *   optimization; readiness recorded here is the workflow authority screens
 *   and route guards consult.
 * - Commits the ID transition exactly once per attempt via
 *   `commitIdTransition`.
 * - After a terminal face result that requires ID, no automatic path may
 *   restart face capture: `isPostFaceSuccessFence` fences camera screens
 *   until an explicit user action begins a new attempt (`beginAttempt`) or a
 *   flow exit resets the owner (`reset`).
 *
 * This module is intentionally a singleton outside React so it survives
 * camera-screen render churn; components subscribe through
 * `useFaceToIdTransition`.
 */

import type { StoredVerificationData } from '../utils/selfieStorage';
import { isFaceOnlyComparison } from '../services/faceMatchRecovery';
import { fetchBestSelfieHandoff } from '../utils/selfieStorage';
import type {
  SelfieArtifactStatus,
  SelfieHandoffRetryTarget,
} from '../types/pipelineRecovery';
import {
  SELFIE_HANDOFF_INTERNAL_ERROR,
  SELFIE_HANDOFF_UNAVAILABLE,
  SelfieHandoffExpiredError,
  SelfieHandoffFetchError,
  SelfieHandoffUnavailableError,
} from '../types/pipelineRecovery';
import type { ScreenType } from '../types/screens';
import { logger } from '../utils/logger';
import {
  awaitsSelfieHandoff,
  type LifecycleProjection,
  verificationLifecycleService,
} from '../services/VerificationLifecycleService';

const transitionLogger = logger.clone({
  labels: { component: 'FaceToIdTransition' },
});

export type FaceHandoffStatus =
  | 'idle'
  | 'resolving'
  | 'ready'
  | 'failed'
  | 'projected';

export interface FaceHandoffFailure {
  code:
    | typeof SELFIE_HANDOFF_UNAVAILABLE
    | typeof SELFIE_HANDOFF_INTERNAL_ERROR;
  artifactStatus: SelfieArtifactStatus | 'internal_error';
  retryTarget: SelfieHandoffRetryTarget | null;
  retryable: boolean;
  terminal: boolean;
  attempts: number;
}

export interface FaceToIdTransitionState {
  /** Monotonic id of the active face capture attempt. */
  attemptId: number;
  /** First terminal face result for the active attempt was accepted. */
  terminal: boolean;
  /** The accepted terminal result requires document verification. */
  requiresIdHandoff: boolean;
  sessionId: string | null;
  status: FaceHandoffStatus;
  /** Ready artifact for the active attempt (workflow authority). */
  selfie: StoredVerificationData | null;
  failure: FaceHandoffFailure | null;
  /** Exact Portal response that superseded local artifact ownership. */
  projection: LifecycleProjection | null;
  /** The single allowed ID transition for this attempt was consumed. */
  idTransitionCommitted: boolean;
  /**
   * The handoff completed into a face-only comparison with the ID Verify
   * Engine retained (a face-match selfie retry), so the ID transition goes to
   * processing instead of the ID request.
   */
  faceOnlyComparison: boolean;
}

/** Screens that restart or run face capture; fenced after terminal success. */
const FACE_CAPTURE_SCREENS: ReadonlySet<ScreenType> = new Set([
  'camera-access',
  'camera-setup',
]);

/** Thrown when an async resolution outlives the attempt that started it. */
export class AttemptSupersededError extends Error {
  constructor() {
    super('Face capture attempt was superseded');
    this.name = 'AttemptSupersededError';
  }
}

/**
 * The artifact failure lost an ownership race to a newer Portal projection.
 * Callers must project this lifecycle response and must not reinterpret the
 * earlier artifact exception as the current recovery action.
 */
export class AuthoritativeHandoffProjection extends Error {
  constructor(readonly lifecycle: LifecycleProjection) {
    super('Portal selected a different lifecycle action');
    this.name = 'AuthoritativeHandoffProjection';
  }
}

function createIdleState(attemptId: number): FaceToIdTransitionState {
  return {
    attemptId,
    terminal: false,
    requiresIdHandoff: false,
    sessionId: null,
    status: 'idle',
    selfie: null,
    failure: null,
    projection: null,
    idTransitionCommitted: false,
    faceOnlyComparison: false,
  };
}

function mapResolutionFailure(error: unknown): FaceHandoffFailure {
  if (error instanceof SelfieHandoffUnavailableError) {
    return {
      code: error.code,
      artifactStatus: error.artifactStatus,
      retryTarget: error.retryTarget,
      retryable: error.retryable,
      terminal: error.terminal,
      attempts: error.attempts,
    };
  }

  if (error instanceof SelfieHandoffExpiredError) {
    return {
      code: error.code,
      artifactStatus: error.artifactStatus,
      retryTarget: null,
      retryable: error.retryable,
      terminal: error.terminal,
      attempts: 1,
    };
  }

  return {
    code: SELFIE_HANDOFF_INTERNAL_ERROR,
    artifactStatus: 'internal_error',
    retryTarget: null,
    retryable: false,
    terminal: false,
    attempts: 1,
  };
}

/**
 * Project an observed artifact failure through the lifecycle response that
 * won the report race. A conflict response is authoritative just like a
 * successful command response: local state may describe the recovery Portal
 * selected, but it may not preserve a stale failure over continued work.
 */
type ProjectedHandoffRetry = FaceHandoffFailure & {
  artifactStatus: 'missing' | 'temporarily_unavailable';
  retryTarget: SelfieHandoffRetryTarget;
  retryable: true;
  terminal: false;
};

function failureForLifecycleProjection(
  observed: FaceHandoffFailure,
  lifecycle: LifecycleProjection,
): ProjectedHandoffRetry | null {
  switch (lifecycle.recovery.action) {
    case 'RETRY_OPERATION': {
      const targetStage = lifecycle.recovery.targetStage;
      if (
        (targetStage !== 'FACE_CAPTURE' &&
          targetStage !== 'SELFIE_HANDOFF') ||
        lifecycle.attempt?.status !== 'ACTIVE' ||
        lifecycle.attempt.stage !== 'SELFIE_HANDOFF' ||
        lifecycle.attempt.operation?.kind !== 'SELFIE_ARTIFACT_FETCH' ||
        lifecycle.attempt.operation.status !== 'RETRYABLE_FAILURE'
      ) {
        return null;
      }
      const requiresRecapture = targetStage === 'FACE_CAPTURE';
      return {
        code: SELFIE_HANDOFF_UNAVAILABLE,
        artifactStatus: requiresRecapture
          ? 'missing'
          : 'temporarily_unavailable',
        retryTarget: requiresRecapture
          ? 'selfie_capture'
          : 'selfie_handoff',
        retryable: true,
        terminal: false,
        attempts: observed.attempts,
      };
    }
    case 'START_SUCCESSOR_ATTEMPT':
    case 'START_NEW_SESSION':
    case 'CONTINUE':
    case 'WAIT':
    case 'RETURN':
    case 'NONE':
      return null;
  }
}

/** Tries, then two retries, for re-establishing an interrupted handoff. */
const RESUME_ATTEMPTS = 3;
const RESUME_RETRY_DELAY_MS = 1_000;

const HANDOFF_ESTABLISHED_STAGES = new Set([
  'SELFIE_HANDOFF',
  'DOCUMENT_CONSENT',
  'DOCUMENT_CAPTURE',
  'DOCUMENT_PROCESSING',
]);

/** The same attempt already moved past the accepted face result. */
const hasEstablishedHandoff = (projection: LifecycleProjection): boolean =>
  projection.sessionStatus === 'PENDING' &&
  projection.attempt?.status === 'ACTIVE' &&
  HANDOFF_ESTABLISHED_STAGES.has(projection.attempt.stage) &&
  // A recovery offer is Portal's answer, not progress.
  (projection.recovery.action === 'WAIT' || projection.recovery.action === 'CONTINUE');

interface HandoffLifecycleOwner {
  getCurrent(): LifecycleProjection | null;
  acceptFaceCaptureResultForHandoff(): Promise<LifecycleProjection>;
  completeSelfieHandoff(): Promise<LifecycleProjection>;
  reconcile(): Promise<LifecycleProjection>;
  reportOperationFailureOnce(params: {
    failureCode: string;
    failureClass?: string;
  }): Promise<LifecycleProjection>;
}

export class FaceToIdTransition {
  private state: FaceToIdTransitionState = createIdleState(0);
  private listeners: Array<(state: FaceToIdTransitionState) => void> = [];
  private inFlight: Promise<StoredVerificationData> | null = null;
  private inFlightAttemptId = -1;

  constructor(
    private readonly lifecycle: HandoffLifecycleOwner = verificationLifecycleService,
  ) {}

  /** Stable snapshot reference for useSyncExternalStore. */
  getState = (): FaceToIdTransitionState => {
    return this.state;
  };

  subscribe = (
    listener: (state: FaceToIdTransitionState) => void
  ): (() => void) => {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  };

  getAttemptId(): number {
    return this.state.attemptId;
  }

  /**
   * Start a new face capture attempt. This is the only way to clear a
   * terminal latch and must be reached through explicit user intent
   * (fresh capture mount, user-initiated recapture, or flow restart).
   */
  beginAttempt(): number {
    const attemptId = this.state.attemptId + 1;
    this.inFlight = null;
    this.inFlightAttemptId = -1;
    this.setState(createIdleState(attemptId));
    transitionLogger.info('Face capture attempt started', {
      labels: { attemptId: `${attemptId}` },
    });
    return attemptId;
  }

  /**
   * Accept the first terminal face result for the active attempt.
   * Returns false for duplicate results and for results belonging to a
   * superseded attempt; callers must ignore the result in both cases.
   */
  acceptTerminalResult(
    attemptId: number,
    result: { requires_id_verification?: boolean; session_id?: string }
  ): boolean {
    if (attemptId !== this.state.attemptId) {
      transitionLogger.warn('Ignoring terminal result from superseded attempt', {
        labels: {
          attemptId: `${attemptId}`,
          activeAttemptId: `${this.state.attemptId}`,
        },
      });
      return false;
    }

    if (this.state.terminal) {
      transitionLogger.warn('Ignoring duplicate terminal face result', {
        labels: { attemptId: `${attemptId}` },
      });
      return false;
    }

    this.setState({
      ...this.state,
      terminal: true,
      requiresIdHandoff: !!result.requires_id_verification,
      sessionId: result.session_id ?? this.state.sessionId,
    });
    return true;
  }

  isTerminalForAttempt(attemptId: number): boolean {
    return attemptId === this.state.attemptId && this.state.terminal;
  }

  /**
   * True from the moment a terminal face result requiring ID is accepted
   * until the attempt is explicitly released (`beginAttempt`/`reset`).
   * The fence depends only on the accepted terminal ID requirement — never
   * on handoff resolution status — so no automatic behavior can restart
   * face capture even before (or without) a resolution attempt.
   */
  isPostFaceSuccessFence(): boolean {
    return this.state.terminal && this.state.requiresIdHandoff;
  }

  /** Browser history may request a transition but not bypass the fence. */
  blocksHistoryNavigation(target: ScreenType): boolean {
    return this.isPostFaceSuccessFence() && FACE_CAPTURE_SCREENS.has(target);
  }

  hasReadyHandoff(): boolean {
    return this.state.status === 'ready' && !!this.state.selfie;
  }

  getReadySelfie(): StoredVerificationData | null {
    return this.state.status === 'ready' ? this.state.selfie : null;
  }

  /**
   * Resolve the required handoff artifact for the active attempt through one
   * bounded, coalesced read sequence (BestSelfieService owns timeouts,
   * backoff, and in-flight coalescing). Browser-cached content is never used
   * to authorize or replace this Portal read. Throws:
   * - AttemptSupersededError when the attempt changed (caller discards),
   * - SelfieHandoffUnavailableError for classified recoverable outcomes,
   * - AuthoritativeHandoffProjection when Portal selected another action,
   * - SelfieHandoffFetchError for internal/config/auth failures.
   */
  async resolveHandoff(attemptId: number): Promise<StoredVerificationData> {
    this.assertActiveAttempt(attemptId);

    if (this.state.status === 'ready' && this.state.selfie) {
      return this.state.selfie;
    }

    if (this.inFlight && this.inFlightAttemptId === attemptId) {
      return this.inFlight;
    }

    const sessionId = this.state.sessionId;
    if (!sessionId) {
      const failure = new SelfieHandoffFetchError();
      this.setState({
        ...this.state,
        status: 'failed',
        failure: mapResolutionFailure(failure),
        projection: null,
      });
      throw failure;
    }

    const run = this.performResolve(attemptId, sessionId);
    this.inFlight = run;
    this.inFlightAttemptId = attemptId;

    try {
      return await run;
    } finally {
      if (this.inFlight === run) {
        this.inFlight = null;
        this.inFlightAttemptId = -1;
      }
    }
  }

  /**
   * Consume the single allowed ID transition for the attempt. Returns true
   * exactly once, and only when the artifact is ready.
   */
  commitIdTransition(attemptId: number): boolean {
    if (attemptId !== this.state.attemptId) {
      return false;
    }
    if (this.state.status !== 'ready' || !this.state.selfie) {
      return false;
    }
    if (this.state.idTransitionCommitted) {
      return false;
    }

    this.setState({ ...this.state, idTransitionCommitted: true });
    return true;
  }

  /**
   * Rehydrate the transition after a refresh/remount that restored an
   * ID-flow screen. The restored screen implies a completed face step, so
   * the attempt is latched terminal and resolution starts immediately;
   * route guards render a bounded pending state until it settles.
   */
  beginRestoredIdFlow(sessionId: string): void {
    const attemptId = this.beginAttempt();
    this.setState({
      ...this.state,
      terminal: true,
      requiresIdHandoff: true,
      sessionId,
    });

    void this.resolveHandoff(attemptId).catch((error: unknown) => {
      // State already reflects the failure; route-level UI reacts to it.
      transitionLogger.warn('Restored ID flow could not resolve the handoff', {
        labels: {
          errorClass: error instanceof Error ? error.name : typeof error,
        },
      });
    });
  }

  /**
   * Reset after the flow exits the face-to-ID corridor: document
   * verification settled, the user explicitly restarted, or the guard
   * routed an unsupported state to failure.
   */
  reset(): void {
    this.inFlight = null;
    this.inFlightAttemptId = -1;
    this.setState(createIdleState(this.state.attemptId + 1));
  }

  /**
   * Establish the selfie handoff for a face result Portal already accepted,
   * exactly as the interrupted page would have. Runs inside the resolve
   * sequence, so the route sees it as pending and cannot start another. A
   * refusal whose projection shows the handoff already established is
   * progress; a transient failure is retried briefly, because settling as
   * failed would send the customer back to retake a selfie Portal holds.
   */
  private async resumeAcceptedFaceResult(attemptId: number): Promise<void> {
    for (let attempt = 1; ; attempt += 1) {
      try {
        await this.lifecycle.acceptFaceCaptureResultForHandoff();
        this.assertActiveAttempt(attemptId);
        return;
      } catch (error) {
        this.assertActiveAttempt(attemptId);
        const authoritative =
          error instanceof Error && 'lifecycle' in error
            ? (error as Error & { lifecycle?: LifecycleProjection }).lifecycle
            : undefined;
        if (authoritative) {
          if (hasEstablishedHandoff(authoritative)) return;
          this.setState({
            ...this.state,
            status: 'projected',
            selfie: null,
            failure: null,
            projection: authoritative,
            idTransitionCommitted: false,
          });
          throw new AuthoritativeHandoffProjection(authoritative);
        }
        if (attempt >= RESUME_ATTEMPTS) {
          this.setState({
            ...this.state,
            status: 'failed',
            failure: mapResolutionFailure(error),
            projection: null,
          });
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, RESUME_RETRY_DELAY_MS * attempt));
        this.assertActiveAttempt(attemptId);
      }
    }
  }

  private async performResolve(
    attemptId: number,
    sessionId: string
  ): Promise<StoredVerificationData> {
    this.setState({
      ...this.state,
      status: 'resolving',
      failure: null,
      projection: null,
    });

    // After a reload Portal may hold the accepted face result without the
    // handoff it leads to; establish it before reading the artifact.
    const current = this.lifecycle.getCurrent();
    if (current && awaitsSelfieHandoff(current)) {
      await this.resumeAcceptedFaceResult(attemptId);
    }

    let data: StoredVerificationData | null;
    try {
      data = await fetchBestSelfieHandoff(sessionId);
      if (!data || !data.best_selfie) {
        // Missing session/config resolves to null; treat as internal.
        throw new SelfieHandoffFetchError();
      }
    } catch (error) {
      if (error instanceof AttemptSupersededError) {
        throw error;
      }
      if (this.state.attemptId !== attemptId) {
        throw new AttemptSupersededError();
      }
      return this.settleArtifactFailure(attemptId, error);
    }

    this.assertActiveAttempt(attemptId);
    let handedOff: LifecycleProjection | undefined;
    try {
      handedOff = await this.lifecycle.completeSelfieHandoff();
    } catch (error) {
      if (this.state.attemptId !== attemptId) {
        throw new AttemptSupersededError();
      }
      this.setState({
        ...this.state,
        status: 'failed',
        failure: mapResolutionFailure(error),
        projection: null,
      });
      throw error;
    }

    this.assertActiveAttempt(attemptId);
    this.setState({
      ...this.state,
      status: 'ready',
      selfie: data,
      projection: null,
      faceOnlyComparison: isFaceOnlyComparison(handedOff),
    });
    return data;
  }

  private async settleArtifactFailure(
    attemptId: number,
    error: unknown,
  ): Promise<never> {
    const observedFailure = mapResolutionFailure(error);
    let authoritative: LifecycleProjection | null = null;
    let reportingError: unknown;
    try {
      if (error instanceof SelfieHandoffExpiredError) {
        // The 410 already describes durable session expiry. Reconcile the
        // authoritative projection, but never manufacture an operation
        // failure or recovery action for it.
        await this.lifecycle.reconcile().catch(() => undefined);
      } else {
        authoritative = await this.lifecycle.reportOperationFailureOnce({
          failureCode: observedFailure.code,
          failureClass: observedFailure.artifactStatus,
        });
      }
    } catch (reportError) {
      reportingError = reportError;
    }

    this.assertActiveAttempt(attemptId);
    if (!authoritative) {
      this.setState({
        ...this.state,
        status: 'failed',
        failure: observedFailure,
        projection: null,
      });
      throw reportingError ?? error;
    }

    const projectedFailure = failureForLifecycleProjection(
      observedFailure,
      authoritative,
    );
    if (projectedFailure) {
      this.setState({
        ...this.state,
        status: 'failed',
        failure: projectedFailure,
        projection: null,
      });
      throw new SelfieHandoffUnavailableError(
        projectedFailure.artifactStatus,
        projectedFailure.retryTarget,
        projectedFailure.attempts,
      );
    }

    // The local artifact fact no longer owns recovery. Keep the terminal
    // capture fence until the caller has projected Portal's selected route;
    // this prevents a render or history race from reopening the old attempt.
    this.setState({
      ...this.state,
      status: 'projected',
      selfie: null,
      failure: null,
      projection: authoritative,
      idTransitionCommitted: false,
    });
    throw new AuthoritativeHandoffProjection(authoritative);
  }

  private assertActiveAttempt(attemptId: number): void {
    if (attemptId !== this.state.attemptId) {
      throw new AttemptSupersededError();
    }
  }

  private setState(nextState: FaceToIdTransitionState): void {
    this.state = nextState;
    this.listeners.forEach((listener) => listener(this.state));
  }
}

export const faceToIdTransition = new FaceToIdTransition();
